import { Switch, Tooltip } from "@mui/material";
import {
  AppConnectionCard,
  AppConnectionContent,
  AppConnectionLeft,
  AppConnectionNameRow,
  AppIcon,
  AppName,
  SwitchContainer,
} from "./AppConnection.styles";
import { ConnectedAppsKeys, getAppIcon } from "./app-connection-helpers";
import { Typography } from "@avenue-8/sidekick-ui";
import { useUIStore } from "@store/ui.store";
import { useCallback } from "react";
import { useIsCrmEnabled } from "@shared/access-control/useIsCrmEnabled";
import { toast } from "@shared/services/toast";

type AppConnectionProps = {
  app: {
    id: string;
    connection: string;
    name: string;
    description?: string;
  };
  handleConnectApp: (app: {
    id: string | undefined;
    connect: boolean;
    swapContext?: { disconnectedAppName: string; newAppName: string };
  }) => Promise<void>;
  size?: "small" | "large";
  setConnection: (
    connection: "connected" | "not-connected" | "expired" | "connecting",
  ) => void;
  highlighted?: boolean;
  conflictingApp?: { id: string; name: string };
  onResolveConflict?: (conflictingAppId: string) => Promise<void>;
  disabled?: boolean;
  disabledTooltip?: string;
};

export const AppConnection = (props: AppConnectionProps) => {
  const {
    app,
    handleConnectApp,
    size = "large",
    setConnection,
    highlighted = false,
    conflictingApp,
    onResolveConflict,
    disabled = false,
    disabledTooltip,
  } = props;
  const confirmDialog = useUIStore((state) => state.confirmDialog);

  const onChange = useCallback(
    async (_e: React.SyntheticEvent, checked: boolean) => {
      if (!checked) {
        const shouldDisconnect = await confirmDialog({
          title: "Disable Integration",
          content: `Are you sure you want to disable ${app.name}?`,
          confirmButtonText: "Yes, Disable",
          cancelButtonText: "Cancel",
        });

        if (!shouldDisconnect) return;
      } else if (conflictingApp) {
        const shouldSwitch = await confirmDialog({
          title: "Switch Email Integration",
          content: (
            <div>
              <Typography>
                Only one email integration can be connected at a time.
              </Typography>
              <Typography mt={1}>
                Enabling <strong>{app.name}</strong> will disconnect{" "}
                <strong>{conflictingApp.name}</strong>. Continue?
              </Typography>
            </div>
          ),
          confirmButtonText: `Switch to ${app.name}`,
          cancelButtonText: "Cancel",
        });

        if (!shouldSwitch) return;

        try {
          await onResolveConflict?.(conflictingApp.id);
        } catch {
          toast.error(
            `Failed to disconnect ${conflictingApp.name}. Please try again.`,
            { toastId: `disconnect-failed-${conflictingApp.id}` },
          );
          return;
        }
      }

      setConnection(checked ? "connecting" : "not-connected");
      try {
        await handleConnectApp({
          id: app.id,
          connect: checked,
          swapContext: conflictingApp
            ? {
                disconnectedAppName: conflictingApp.name,
                newAppName: app.name,
              }
            : undefined,
        });
      } catch {
        setConnection(checked ? "not-connected" : "connected");
        toast.error(
          `Failed to ${checked ? "connect" : "disconnect"} ${app.name}. Please try again.`,
          { toastId: `connect-app-failed-${app.id}` },
        );
      }
    },
    [
      app.id,
      app.name,
      confirmDialog,
      conflictingApp,
      handleConnectApp,
      onResolveConflict,
      setConnection,
    ],
  );

  const isCrmEnabled = useIsCrmEnabled() ?? false;
  const description =
    app.id === "google" && isCrmEnabled && app.description
      ? `${app.description} We'll also read your inbox to identify contacts and leads for your CRM.`
      : app.description;

  return (
    <AppConnectionCard key={app.id} size={size} highlighted={highlighted}>
      <AppConnectionContent size={size}>
        <AppConnectionLeft>
          <AppConnectionNameRow>
            <AppIcon size={size}>
              {getAppIcon(app.id as ConnectedAppsKeys)}
            </AppIcon>
            <AppName size={size}>{app.name}</AppName>
          </AppConnectionNameRow>
          {description && (
            <Typography variant="caption" color="textSecondary">
              {description}
            </Typography>
          )}
        </AppConnectionLeft>

        <SwitchContainer>
          {disabled ? (
            <Tooltip title={disabledTooltip} arrow>
              <span>
                <Switch
                  color="primary"
                  onChange={onChange}
                  checked={
                    app.connection === "connected" ||
                    app.connection === "connecting"
                  }
                  disabled
                />
              </span>
            </Tooltip>
          ) : (
            <Switch
              color="primary"
              onChange={onChange}
              checked={
                app.connection === "connected" ||
                app.connection === "connecting"
              }
              disabled={app.connection === "connecting"}
            />
          )}
        </SwitchContainer>
      </AppConnectionContent>
    </AppConnectionCard>
  );
};
